import { HttpClient } from "@angular/common/http";
import { Component } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { PlacementArray } from "@ng-bootstrap/ng-bootstrap/util/positioning";
import {
  AlertController,
  IonicPage,
  LoadingController,
  NavController,
  NavParams,
  ViewController,
} from "ionic-angular";
import moment from "moment-timezone";
import { DiagnosisService } from "providers/patient-charts/diagnosis-service";
import { Observable } from "rxjs";
import { forkJoin } from "rxjs/observable/forkJoin";
import PatientNote from "../../../../models/patient-chart/notes/note";
import { ConfigurationService } from "../../../../providers/configuration/configuration.service";
import { ReferralsServiceProvider } from "../../../../providers/patient-charts/referrals-service";
import { encountersValidationMessage } from "../../../../validators/validationMessages";

@IonicPage()
@Component({
  selector: "edit-visit-time",
  templateUrl: "edit-visit-time.html",
})
export class EditVisitTime {
  editVisitForm: FormGroup;

  spinnerCounter: number = 0;
  spinner: any = null;

  validationMessages = encountersValidationMessage;

  datePlacementPreference: PlacementArray = ["bottom"];
  minDate = new Date(new Date().setFullYear(new Date().getFullYear() - 100));
  minDateFormat = {
    year: this.minDate.getFullYear(),
    month: this.minDate.getMonth() + 1,
    day: this.minDate.getDate(),
  };
  maxDate = new Date();
  maxDateFormat = {
    year: this.maxDate.getFullYear(),
    month: this.maxDate.getMonth() + 1,
    day: this.maxDate.getDate(),
  };

  meridian: boolean = true;

  encounterData: PatientNote = null;
  patientChartId: any;
  patientId: number;
  apiUrl: string;
  visit: Visit;
  diagnosisPlanCardToggle: boolean = false;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public loadingCtrl: LoadingController,
    public referralService: ReferralsServiceProvider,
    private alertCtrl: AlertController,
    private configurationService: ConfigurationService,
    private diagnosisService: DiagnosisService,
    private http: HttpClient
  ) {
    this.apiUrl = configurationService.getConfig().apiUrl;
  }

  ionViewDidLoad() {
    this.visit = this.navParams.data.visit;
    this.patientId = this.navParams.data.patientId;
    this.diagnosisPlanCardToggle = this.navParams.data.diagnosisPlanCardToggle;
    let startDateTime = this.visit.startDate;
    let endDateTime = this.visit.endDate;
    this.editVisitForm.get("startDate").setValue(new moment(startDateTime));

    this.editVisitForm.get("endDate").setValue(new moment(endDateTime));

    var startTime: Object = {
      hour: new moment(startDateTime).format("HH"),
      minute: new moment(startDateTime).format("mm"),
    };
    var endTime: Object = {
      hour: new moment(endDateTime).format("HH"),
      minute: new moment(endDateTime).format("mm"),
    };

    this.editVisitForm.get("startTime").setValue(startTime);
    this.editVisitForm.get("endTime").setValue(endTime);
  }

  ionViewWillLoad() {
    this.editVisitForm = new FormGroup({
      startDate: new FormControl("", Validators.required),
      startTime: new FormControl("", Validators.required),
      endDate: new FormControl("", Validators.required),
      endTime: new FormControl("", Validators.required),
    });
  }

  formatData(data) {
    return {
      newVisitStartDateTime: this.formatDate(data.startDate, data.startTime),
      newVisitEndDateTime: this.formatDate(data.endDate, data.endTime),
    };
  }

  invalidDate(startDate: any, startTime: any, endDate: any, endTime: any) {
    let start = new moment(this.formatDate(startDate, startTime)).utc();
    let end = new moment(this.formatDate(endDate, endTime)).utc();
    if (end.isBefore(start)) return true;
    return false;
  }

  save(values) {
    if (
      this.invalidDate(
        values.startDate,
        values.startTime,
        values.endDate,
        values.endTime
      )
    ) {
      this.dateOverlapMessage();
      return;
    }

    const body = this.formatData(values);

    const diagnosesStartDate = this.formatDateTimeForDiagnoses(
      values.startDate,
      values.startTime
    );
    const diagnosesEndDate = this.formatDateTimeForDiagnoses(
      values.endDate,
      values.endTime
    );
    let updateVisitDiagnosesDateTime =
      this.diagnosisService.updateVisitDiagnosesDateTime(
        this.patientId,
        this.visit._id,
        diagnosesStartDate,
        diagnosesEndDate
      );
    let updateVisitDateTime = this.updateVisitDateTime(body);

    let updateVisitObservables = [updateVisitDateTime];

    if (this.diagnosisPlanCardToggle) {
      updateVisitObservables.push(updateVisitDiagnosesDateTime);
    }
    forkJoin(updateVisitObservables).subscribe((response) => {
      this.viewCtrl.dismiss(response[0]);
    });
  }

  updateVisitDateTime(body: object): Observable<any> {
    return new Observable((observer) => {
      try {
        this.increaseSpinnerCount("Loading...");
        let url = `${this.apiUrl}/patient-charts/visits/${this.visit._id}/visitTimes`;
        this.http.post(url, body).subscribe(
          (data) => {
            this.decreaseSpinnerCount();
            observer.next(data);
            observer.complete();
          },
          (err) => {
            this.decreaseSpinnerCount();
            observer.error(err);
            observer.complete();
          }
        );
        let spsurl = `${this.configurationService.getConfig().spsUrl}/visits/${
          this.visit._id
        }/regenreports`;
        this.http.post(spsurl, {}).subscribe((data) => {
          console.log("Regen reports", data);
        });
      } catch (error) {
        this.decreaseSpinnerCount();
        console.log("Error during update visit date/time.", error);
      }
    });
  }

  close() {
    this.viewCtrl.dismiss();
  }

  // incremental loader function
  increaseSpinnerCount(message: string = "Creating Assessment...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  // decremental loader function
  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  private formatDate(date: any, time: any): string {
    let test =
      moment(date).format("YYYY-MM-DD") +
      "T" +
      this.convertToDoubleDigit(time.hour) +
      ":" +
      this.convertToDoubleDigit(time.minute) +
      ":00Z";
    return test;
  }

  private formatDateTimeForDiagnoses(sampleDate, sampleTime) {
    // Parse the input
    const dateMoment = moment(sampleDate);

    // Set the specific time
    dateMoment.set({
      hour: sampleTime.hour,
      minute: sampleTime.minute,
      second: sampleTime.second,
    });

    // Format the date in the desired format
    const formattedDate = dateMoment.utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
    return formattedDate;
  }

  private convertToDoubleDigit(n) {
    let temp = n + ""; //Convert to a string
    if (temp.length == 1) {
      return "0" + temp;
    }
    return temp;
  }

  dateOverlapMessage() {
    let alert = this.alertCtrl.create({
      title: "Invalid Date",
      message: `End Date Time must be after Start Date Time.`,
      buttons: [
        {
          text: "Ok",
        },
      ],
    });
    alert.present();
  }
}

export interface Visit {
  _id: number;
  startDate: string;
  endDate: string;
}



// WEBPACK FOOTER //
// ./src/pages/assessment/visits/edit-visit-time/edit-visit-time.ts